import { Link } from 'react-router-dom';
import { Ruler, Sparkles, ArrowRight, Heart } from 'lucide-react';
import Seo from '@/components/Seo';

const VIDEO_URL =
  'https://customer-assets.emergentagent.com/job_morse-message-store/artifacts/i4sepvg9_create_a_video_showing_how_to.mp4';

const STEPS = [
  {
    n: 1,
    title: 'Find a piece of string',
    body:
      'A length of soft cotton string, a thin ribbon, or even a strip of paper works. Anything flexible that holds its shape.',
  },
  {
    n: 2,
    title: 'Wrap it gently around your wrist',
    body:
      'Place the string just below your wrist bone, where you would naturally wear a bracelet. Snug against the skin — not tight, not loose.',
  },
  {
    n: 3,
    title: 'Mark where it overlaps',
    body:
      'Pinch the point where the two ends of the string meet, or mark it with a pen. That mark is your true wrist size.',
  },
  {
    n: 4,
    title: 'Lay it flat against a ruler',
    body:
      'Measure in centimetres from the start of the string to your mark. That number is your wrist circumference — and that is your size.',
  },
];

const SIZES = [
  { wrist: '14 – 15 cm', bracelet: '17 cm', vibe: 'Snug, close to the skin' },
  { wrist: '15 – 16 cm', bracelet: '18 cm', vibe: 'Comfortable, classic fit' },
  { wrist: '16 – 17 cm', bracelet: '19 cm', vibe: 'Relaxed, room to move' },
  { wrist: '17 – 18 cm', bracelet: '20 cm', vibe: 'Generous, sits lower on the wrist' },
  { wrist: '18 – 19 cm', bracelet: '21 cm', vibe: 'Loose and easy' },
  { wrist: '19 – 21 cm', bracelet: '22 – 23 cm', vibe: 'Best for larger wrists or layering' },
];

export default function SizingGuidePage() {
  return (
    <div className="bg-[#FAF9F6] min-h-screen" data-testid="sizing-guide-page">
      <Seo title="Bracelet sizing guide" description={"Measure your wrist in 60 seconds. A short guide with a video so your Whispering Wristwear bracelet fits the way you want it."} path="/sizing-guide" />
      {/* Hero */}
      <section className="container-custom px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-10 max-w-6xl mx-auto">
        <Link
          to="/"
          className="text-caption text-[#84A98C] hover:text-[#52796F] transition-colors inline-flex items-center gap-2"
          data-testid="sizing-guide-breadcrumb"
        >
          ← Back to home
        </Link>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <span className="text-caption text-[#D4AF37] inline-flex items-center gap-2 mb-3">
              <Ruler className="w-4 h-4" strokeWidth={1.5} />
              Sizing guide
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#354F52] leading-tight">
              How to measure your wrist
            </h1>
            <p className="mt-5 text-[#52796F] text-base md:text-lg leading-relaxed max-w-xl">
              The simplest way to find your perfect bracelet size — a piece of string,
              a ruler, and one quiet minute. Watch Lindy show you exactly how.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right text-[#84A98C] text-sm italic">
            Made in Cyprus · sized to your wrist
          </div>
        </div>
      </section>

      {/* Video + steps */}
      <section className="container-custom px-6 md:px-12 lg:px-24 pb-16 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Video */}
          <div className="lg:col-span-7">
            <div
              className="rounded-sm overflow-hidden bg-[#CAD2C5]/30 border border-[#CAD2C5]/40"
              data-testid="sizing-guide-video-wrapper"
            >
              <video
                src={VIDEO_URL}
                controls
                preload="metadata"
                playsInline
                className="w-full h-auto block"
                data-testid="sizing-guide-video"
              >
                <track kind="captions" />
                Sorry, your browser doesn&apos;t support embedded videos.
              </video>
            </div>
            <p className="text-xs text-[#84A98C] mt-3 italic">
              Tip: keep the string snug but not tight — your bracelet should feel like
              a small reminder, never a restriction.
            </p>
          </div>

          {/* Steps */}
          <div className="lg:col-span-5">
            <h2 className="font-serif text-2xl text-[#354F52] mb-6">
              Four small steps
            </h2>
            <ol className="space-y-5" data-testid="sizing-guide-steps">
              {STEPS.map((s) => (
                <li key={s.n} className="flex gap-4">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-serif text-lg">
                    {s.n}
                  </div>
                  <div className="pt-1">
                    <h3 className="font-serif text-lg text-[#354F52] leading-snug">
                      {s.title}
                    </h3>
                    <p className="text-sm text-[#52796F] leading-relaxed mt-1">
                      {s.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Size chart */}
      <section className="bg-white border-y border-[#CAD2C5]/40 py-16">
        <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <h2 className="font-serif text-3xl text-[#354F52] mb-3">
              From your wrist to your bracelet
            </h2>
            <p className="text-[#52796F] leading-relaxed">
              Most of our bracelets are made in 17 – 23 cm sizes. Once you know your wrist
              measurement, pick the bracelet length that matches the fit you like. If you&apos;re
              between two sizes, go up for comfort.
            </p>
          </div>

          <div
            className="overflow-hidden rounded-sm border border-[#CAD2C5]/60"
            data-testid="sizing-guide-table"
          >
            <table className="w-full text-left">
              <thead className="bg-[#F5F5F0] text-[#354F52]">
                <tr>
                  <th className="px-5 py-4 text-caption">Your wrist</th>
                  <th className="px-5 py-4 text-caption">Bracelet size</th>
                  <th className="px-5 py-4 text-caption">How it sits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#CAD2C5]/40">
                {SIZES.map((row) => (
                  <tr key={row.wrist} className="bg-white hover:bg-[#FAF9F6] transition-colors">
                    <td className="px-5 py-4 font-serif text-[#354F52]">{row.wrist}</td>
                    <td className="px-5 py-4 font-serif text-[#D4AF37]">{row.bracelet}</td>
                    <td className="px-5 py-4 text-sm text-[#52796F] italic">{row.vibe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-[#84A98C] mt-4 italic">
            Cotton-drawstring bracelets are adjustable — one size sits comfortably from
            roughly 15 to 22 cm.
          </p>
        </div>
      </section>

      {/* Where to go next */}
      <section className="container-custom px-6 md:px-12 lg:px-24 py-16 max-w-6xl mx-auto">
        <h2 className="font-serif text-3xl text-[#354F52] text-center mb-3">
          Ready when you are
        </h2>
        <p className="text-[#52796F] text-center max-w-xl mx-auto mb-12">
          Choose your size in the builder, or browse the collection — every piece is made
          to fit, never adjusted afterwards.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/builder"
            className="group block bg-white border border-[#CAD2C5]/60 rounded-sm p-7 hover:border-[#D4AF37] transition-colors"
            data-testid="sizing-guide-link-builder"
          >
            <Sparkles className="w-5 h-5 text-[#D4AF37] mb-4" strokeWidth={1.5} />
            <h3 className="font-serif text-xl text-[#354F52] mb-2">
              Design your own
            </h3>
            <p className="text-sm text-[#52796F] leading-relaxed mb-4">
              The interactive builder includes a fit calculator and live preview.
            </p>
            <span className="inline-flex items-center gap-2 text-[#52796F] group-hover:text-[#D4AF37] text-sm transition-colors">
              Open the builder
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </span>
          </Link>

          <Link
            to="/products"
            className="group block bg-white border border-[#CAD2C5]/60 rounded-sm p-7 hover:border-[#D4AF37] transition-colors"
            data-testid="sizing-guide-link-shop"
          >
            <Heart className="w-5 h-5 text-[#D4AF37] mb-4" strokeWidth={1.5} />
            <h3 className="font-serif text-xl text-[#354F52] mb-2">
              Shop the collection
            </h3>
            <p className="text-sm text-[#52796F] leading-relaxed mb-4">
              Each bracelet listing lets you choose your length before adding to cart.
            </p>
            <span className="inline-flex items-center gap-2 text-[#52796F] group-hover:text-[#D4AF37] text-sm transition-colors">
              Browse bracelets
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </span>
          </Link>

          <Link
            to="/custom"
            className="group block bg-white border border-[#CAD2C5]/60 rounded-sm p-7 hover:border-[#D4AF37] transition-colors"
            data-testid="sizing-guide-link-custom"
          >
            <Ruler className="w-5 h-5 text-[#D4AF37] mb-4" strokeWidth={1.5} />
            <h3 className="font-serif text-xl text-[#354F52] mb-2">
              Order a custom piece
            </h3>
            <p className="text-sm text-[#52796F] leading-relaxed mb-4">
              Share your wrist size and your secret word — we&apos;ll make it to fit.
            </p>
            <span className="inline-flex items-center gap-2 text-[#52796F] group-hover:text-[#D4AF37] text-sm transition-colors">
              Start a custom order
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
